"use client";
import type { Meeting } from "@/api/calendar";
import { addHours, format, isSameDay, startOfDay } from "date-fns";
import { ar, fr as frLocale } from "date-fns/locale";
import { Plus, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import { gen_color } from "@/utils/colors"; // Assuming this utility exists
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { formatUTCToLocal } from "@/utils/calendar";
import { usePermission } from "@/hooks/use-permission";
import { UserPermission } from "@/constants/permissions";

interface DayViewProps {
  date: Date;
  meetings: Meeting[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onEventClick: (event: any) => void; // Pass original meeting data
  onAddAppointment: () => void;
}

// Interface for event data after initial processing
interface ProcessedEvent {
  id: number | string;
  title: string;
  startDate: Date;
  endDate: Date;
  startHour: number;
  endHour: number;
  originalData: Meeting;
}

// Interface for event data including layout information
interface LaidOutEvent extends ProcessedEvent {
  col: number;      // Column index (0-based) within its overlap group
  numCols: number;  // Total number of columns in its overlap group
}

// Helper function to check if two events overlap
function eventsOverlap(eventA: ProcessedEvent, eventB: ProcessedEvent): boolean {
  // Add a small buffer to handle events ending exactly when another starts
  const buffer = 0.001;
  return eventA.startHour < eventB.endHour - buffer && eventA.endHour > eventB.startHour + buffer;
}

// Function to calculate layout (column assignments) for events
function calculateLayout(events: ProcessedEvent[]): LaidOutEvent[] {
  if (!events || events.length === 0) {
    return [];
  }

  // 1. Sort events primarily by start time, secondarily by duration (longer events first)
  const sortedEvents = [...events].sort((a, b) => {
    if (a.startHour !== b.startHour) {
      return a.startHour - b.startHour;
    }
    return (b.endHour - b.startHour) - (a.endHour - a.startHour);
  });

  // Initialize layout data for each event
  const layout: Array<Omit<LaidOutEvent, keyof ProcessedEvent> & { overlaps: number[] }> = sortedEvents.map(() => ({
    col: -1,      // -1 indicates not yet placed
    numCols: 1,
    overlaps: [], // Store indices of overlapping events
  }));

  // 2. Detect overlaps and build conflict graph (implicitly via overlaps array)
  for (let i = 0; i < sortedEvents.length; i++) {
    for (let j = i + 1; j < sortedEvents.length; j++) {
      if (eventsOverlap(sortedEvents[i], sortedEvents[j])) {
        layout[i].overlaps.push(j);
        layout[j].overlaps.push(i);
      }
    }
  }

  // 3. Assign columns iteratively
  for (let i = 0; i < sortedEvents.length; i++) {
    const occupiedColumns: boolean[] = []; // Tracks columns occupied by events overlapping event `i` *that have already been placed*

    // Check columns occupied by previously placed overlapping events
    for (const overlapIndex of layout[i].overlaps) {
      if (overlapIndex < i && layout[overlapIndex].col !== -1) {
        occupiedColumns[layout[overlapIndex].col] = true;
      }
    }

    // Find the first available column
    let currentColumn = 0;
    while (occupiedColumns[currentColumn]) {
      currentColumn++;
    }
    layout[i].col = currentColumn;
  }

  // 4. Determine max columns needed for each overlapping group
  for (let i = 0; i < sortedEvents.length; i++) {
    let maxColsInGroup = 1;
    const overlappingIndices = new Set<number>([i]);
    const queue = [...layout[i].overlaps]; // Start with direct overlaps

    // Breadth-first search to find all connected overlapping events
    while (queue.length > 0) {
      const currentIndex = queue.shift()!;
      if (!overlappingIndices.has(currentIndex)) {
        overlappingIndices.add(currentIndex);
        layout[currentIndex].overlaps.forEach(nextIndex => {
          if (!overlappingIndices.has(nextIndex)) {
            queue.push(nextIndex);
          }
        });
      }
    }

    // Find the max column index + 1 within this connected group
    overlappingIndices.forEach(idx => {
      maxColsInGroup = Math.max(maxColsInGroup, layout[idx].col + 1);
    })

    // Assign the calculated numCols to all events in the group
    overlappingIndices.forEach(idx => {
      layout[idx].numCols = maxColsInGroup;
    });
  }


  // 5. Combine original event data with layout data
  return sortedEvents.map((event, index) => ({
    ...event,
    col: layout[index].col,
    numCols: layout[index].numCols,
  }));
}


export function DayView({
  date,
  meetings,
  onEventClick,
  onAddAppointment,
}: DayViewProps) {
  const { t, i18n } = useTranslation();
  const currentLocale = i18n.language === 'ar' ? ar : frLocale;

  // Default hour range to display (8am to 6pm / 18:00)
  const DEFAULT_START_HOUR = 8;
  const DEFAULT_END_HOUR = 18;

  // Filter meetings for the current day
  const dayMeetings = meetings.filter((meeting) => {
    if (!meeting.start_time) return false;
    try {
      const meetingDate = new Date(meeting.start_time * 1000);
      return isSameDay(meetingDate, date);
    } catch {
      return false;
    }
  });

  // Process meetings into event objects
  const processedEvents: ProcessedEvent[] = dayMeetings.map((meeting) => {
    try {
      const startDate = new Date(meeting.start_time * 1000);
      const endDate = new Date(meeting.end_time ? meeting.end_time * 1000 : meeting.start_time * 1000 + 3600 * 1000);

      const startHour = startDate.getHours() + startDate.getMinutes() / 60;
      let endHour = endDate.getHours() + endDate.getMinutes() / 60;
      if (endHour === 0 && !isSameDay(startDate, endDate)) {
        endHour = 24;
      }

      const minDuration = 15 / 60;
      endHour = Math.max(endHour, startHour + minDuration);
      endHour = Math.min(endHour, 24);

      return {
        id: meeting.id,
        title: meeting.desc || `${t("casePrefix")}: ${meeting.case}`,
        startDate,
        endDate,
        startHour: Math.max(0, startHour),
        endHour,
        originalData: meeting,
      };
    } catch {
      return {
        id: meeting.id || `error-${Math.random()}`,
        title: `Error Processing Meeting`,
        startDate: new Date(),
        endDate: new Date(),
        startHour: 0,
        endHour: 1,
        originalData: meeting,
      };
    }
  }).filter(event => event.startHour < event.endHour);

  // Determine visible hour range based on events and default range
  let visibleStartHour = DEFAULT_START_HOUR;
  let visibleEndHour = DEFAULT_END_HOUR;

  // Expand the visible range if there are events outside the default range
  if (processedEvents.length > 0) {
    const eventStartHours = processedEvents.map(e => Math.floor(e.startHour));
    const eventEndHours = processedEvents.map(e => Math.ceil(e.endHour));

    const minEventHour = Math.min(...eventStartHours);
    const maxEventHour = Math.max(...eventEndHours);

    // Only expand if events are outside default range
    if (minEventHour < DEFAULT_START_HOUR) {
      visibleStartHour = minEventHour;
    }

    if (maxEventHour > DEFAULT_END_HOUR) {
      visibleEndHour = maxEventHour;
    }
  }

  // Create array of only the visible hours
  const visibleHours = Array.from(
    { length: visibleEndHour - visibleStartHour },
    (_, i) => addHours(startOfDay(date), i + visibleStartHour)
  );

  const getEventVerticalPosition = (startHour: number, endHour: number) => {
    // Calculate positions relative to the visible hour range
    const totalVisibleHours = visibleEndHour - visibleStartHour;

    const clampedStartHour = Math.max(startHour, visibleStartHour);
    const clampedEndHour = Math.min(endHour, visibleEndHour);

    const relativeStart = clampedStartHour - visibleStartHour;
    const duration = clampedEndHour - clampedStartHour;

    const top = (relativeStart / totalVisibleHours) * 100;
    const height = (duration / totalVisibleHours) * 100;

    return { top: `${top}%`, height: `${Math.max(height, 1.0)}%` };
  };

  const laidOutEvents = calculateLayout(processedEvents);

  const renderEvent = (laidOutEvent: LaidOutEvent) => {
    const { event, col, numCols } = {
      event: laidOutEvent,
      col: laidOutEvent.col,
      numCols: laidOutEvent.numCols,
    };

    if (event.startHour >= event.endHour) {
      return null;
    }

    const { top, height } = getEventVerticalPosition(
      event.startHour,
      event.endHour
    );

    const columnWidthPercent = 100 / numCols;
    const leftPercent = col * columnWidthPercent;
    const horizontalGap = 2;

    const widthStyle = `calc(${columnWidthPercent}% - ${horizontalGap}px)`;
    const leftStyle = `calc(${leftPercent}% + ${horizontalGap / 2}px)`;

    const classes = gen_color(event.originalData.assigned_to?.name || 'default');

    return (
      <Tooltip key={event.id} delayDuration={100}>
        <TooltipTrigger asChild>
          <div
            className={`${classes.textColorClass} bg-opacity-80 absolute p-1 md:p-2 overflow-hidden rounded cursor-pointer shadow border dark:border-gray-600 ${event.originalData.is_postponed ? "opacity-60" : ""
              }`}
            style={{
              top,
              height,
              left: leftStyle,
              width: widthStyle,
              backgroundColor: classes.backgroundColor,
              borderColor: classes.borderColor,
              zIndex: 10 + col,
            }}
            onClick={() => onEventClick({
              ...event,
              color: { backgroundColor: classes.backgroundColor, borderColor: classes.borderColor },
              text_classes: classes.textColorClass,
            })}
          >
            <div className={`text-xs font-semibold truncate md:text-sm ${event.originalData.is_postponed ? "line-through" : ""}`}>
              {event.originalData.postponed_from && (
                <span className="ml-1 inline-block">
                  <Clock className="w-3 h-3 inline" />
                </span>
              )}
              {event.title}
            </div>
            {(parseFloat(height) > 3) && (
              <div className={`hidden text-xs opacity-90 md:block ${event.originalData.is_postponed ? "line-through" : ""}`}>
                {format(event.startDate, 'HH:mm')}{event.originalData.end_time !== null ? ` - ${format(event.endDate, 'HH:mm')}` : ''}
              </div>
            )}
            {(parseFloat(height) > 3) && (
              <div className={`mt-1 md:text-xs ${event.originalData.is_postponed ? "line-through" : ""}`}>
                {t('assigned_to')}:{event.originalData.assigned_to.name}
              </div>
            )}
            {event.originalData.tribunal_proc_type && (
              <div className={`mt-1 text-[10px] md:text-xs opacity-80 truncate ${event.originalData.is_postponed ? "line-through" : ""}`}>
                {event.originalData.tribunal_proc_type}
              </div>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <div>
            <div className="font-semibold">{event.title}</div>
            <div>
              {formatUTCToLocal(
                new Date(event.originalData.start_time * 1000),
                "yyyy-MM-dd HH:mm"
              )}
              {event.originalData.end_time && (
                <> - {formatUTCToLocal(
                  new Date(event.originalData.end_time * 1000),
                  "HH:mm"
                )}</>
              )}
            </div>
            <div>
              {t('assigned_to')}: {event.originalData.assigned_to.name}
            </div>
            {event.originalData.tribunal_proc_type && (
              <div>
                {event.originalData.tribunal_proc_type}
              </div>
            )}
            {event.originalData.is_postponed && (
              <div className="text-amber-500 dark:text-amber-400">
                {t("meetingPostponed")}
              </div>
            )}
            {event.originalData.postponed_from && (
              <div>
                {t("postponedFromMsg", {
                  date: formatUTCToLocal(
                    new Date(event.originalData.postponed_from.start_time * 1000),
                    "yyyy-MM-dd HH:mm"
                  )
                })}
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    );
  };


  const { can } = usePermission();

  return (
    <TooltipProvider>
      {can(UserPermission.ADD_RENDEZVOUS) && (
        <Button
          onClick={onAddAppointment}
          className="fixed z-50 bg-blue-600 shadow-lg bottom-4 left-4 md:bottom-6 md:left-6 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 p-2 md:p-3 rounded-full"
          aria-label={t("addNewAppointment")}
        >
          <span className="hidden md:inline mr-1">{t("addNewAppointment")}</span>
          <Plus className="w-4 h-4 md:w-5 md:h-5" />
        </Button>
      )}

      <div className="flex flex-col h-full bg-white border border-gray-200 rounded-lg shadow-sm dark:bg-gray-900 dark:border-gray-700 overflow-hidden mt-3">
        {/* Scrollable timeline area */}
        <div className="flex-1 overflow-y-auto">
          {/* Relative container for positioning hours and events */}
          <div className="relative" style={{ height: `${(visibleEndHour - visibleStartHour) * 70}px` }}>

            {/* Hour lines */}
            {visibleHours.map((hour, i) => (
              <div
                key={`hour-${i}`}
                className="absolute flex w-full border-t border-gray-200 dark:border-gray-700"
                style={{ top: `${(i / (visibleEndHour - visibleStartHour)) * 100}%` } as React.CSSProperties}
              >
                {/* Hour Label */}
                <div className={`w-12 md:w-16 pr-1 md:pr-2 text-[10px] md:text-sm text-right text-gray-500 dark:text-gray-400 shrink-0 ${i === 0 ? 'pt-1' : '-mt-3'}`}>
                  {format(hour, "HH:00", { locale: currentLocale })}
                </div>
                {/* Line */}
                <div className="flex-1 h-px"></div>
              </div>
            ))}

            {/* Event Container Area */}
            <div className="absolute top-0 bottom-0 left-12 md:left-16 right-1 md:right-12">
              {/* Render Laid Out Events */}
              {laidOutEvents.map(renderEvent)}
            </div>

            {/* Message when no events */}
            {laidOutEvents.length === 0 && (
              <div className="absolute inset-x-16 inset-y-0 flex items-center justify-center pt-10 text-gray-500 pointer-events-none">
                {t("noAppointments")}
              </div>
            )}
          </div> {/* End Relative container */}
        </div> {/* End Scrollable Area */}
      </div> {/* End Main Container */}
    </TooltipProvider>
  );
}